import { memo } from 'react';

/**
 * FilterTags Component
 *
 * Displays active filters as removable tags/chips
 * Shows search query, price range, sort option, and category filters
 *
 * Optimized with React.memo to prevent unnecessary re-renders
 */

const FilterTags = memo(function FilterTags({
  searchQuery = '',
  priceRange = [0, 800],
  sortBy = 'default',
  selectedCategory = null,
  categories = [],
  onRemoveSearch,
  onRemovePrice,
  onRemoveSort,
  onRemoveCategory,
  onClearAll
}) {
  // Check which filters are active
  const hasSearchFilter = searchQuery && searchQuery.trim().length > 0;
  const hasPriceFilter = priceRange[0] > 0 || priceRange[1] < 800;
  const hasSortFilter = sortBy && sortBy !== 'default';
  const hasCategoryFilter = selectedCategory !== null;

  const activeFiltersCount = [
    hasSearchFilter,
    hasPriceFilter,
    hasSortFilter,
    hasCategoryFilter
  ].filter(Boolean).length;

  // Don't render if no filters are active
  if (activeFiltersCount === 0) {
    return null;
  }

  // Get category name from slug
  const getCategoryName = () => {
    if (!selectedCategory || !categories.length) return '';
    const category = categories.find(cat => cat.slug === selectedCategory);
    return category ? category.name : selectedCategory;
  };

  // Get sort label
  const getSortLabel = () => {
    const sortLabels = {
      'price_asc': 'Price: Low to High',
      'price_desc': 'Price: High to Low',
      'name_asc': 'Name: A to Z',
      'newest': 'Newest First'
    };
    return sortLabels[sortBy] || sortBy;
  };

  return (
    <div className="filter-tags-container">
      <div className="filter-tags-header">
        <span className="filter-tags-label">
          <i className="fas fa-filter me-2"></i>
          Active Filters ({activeFiltersCount})
        </span>
        <button
          className="btn btn-sm btn-link filter-clear-all"
          onClick={onClearAll}
          aria-label="Clear all filters"
        >
          Clear All
        </button>
      </div>

      <div className="filter-tags-list">
        {/* Search Tag */}
        {hasSearchFilter && (
          <div className="filter-tag">
            <i className="fas fa-search me-1"></i>
            <span className="filter-tag-label">Search:</span>
            <span className="filter-tag-value">"{searchQuery}"</span>
            <button
              className="filter-tag-remove"
              onClick={onRemoveSearch}
              aria-label={`Remove search filter: ${searchQuery}`}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Price Range Tag */}
        {hasPriceFilter && (
          <div className="filter-tag">
            <i className="fas fa-pound-sign me-1"></i>
            <span className="filter-tag-label">Price:</span>
            <span className="filter-tag-value">£{priceRange[0]} - £{priceRange[1]}</span>
            <button
              className="filter-tag-remove"
              onClick={onRemovePrice}
              aria-label="Remove price filter"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Sort Tag */}
        {hasSortFilter && (
          <div className="filter-tag">
            <i className="fas fa-sort me-1"></i>
            <span className="filter-tag-label">Sort:</span>
            <span className="filter-tag-value">{getSortLabel()}</span>
            <button
              className="filter-tag-remove"
              onClick={onRemoveSort}
              aria-label="Remove sort filter"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Category Tag */}
        {hasCategoryFilter && (
          <div className="filter-tag">
            <i className="fas fa-tag me-1"></i>
            <span className="filter-tag-label">Category:</span>
            <span className="filter-tag-value">{getCategoryName()}</span>
            <button
              className="filter-tag-remove"
              onClick={onRemoveCategory}
              aria-label={`Remove category filter: ${getCategoryName()}`}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default FilterTags;
